import { Card, CardSection, Field, Input, Toggle, UploadField } from "@/components/ui";
import type { AudioAssets, CopyAssets } from "@/lib/types/config";
import { useBranding, useBuilder } from "../BuilderContext";

const AUDIO_SLOTS: Array<{ key: keyof AudioAssets; label: string; hint: string }> = [
  { key: "welcome", label: "Welcome screen music", hint: "Loops on the attract screen while the booth waits for a guest." },
  { key: "participant_selection", label: "Participant selection audio", hint: "Plays on the acts screen." },
  { key: "result", label: "Result / success sound", hint: "A short sting when the generated image appears." },
  { key: "completion", label: "Completion sound", hint: "Plays on the final thank-you screen." },
  { key: "processing", label: "AI processing music", hint: "Covers the wait while the image generates — this is the longest silence in the flow." },
];

const COPY_SLOTS: Array<{ key: keyof CopyAssets; label: string; placeholder: string; hint?: string }> = [
  { key: "print_button_label", label: "Print button label", placeholder: "Print Photo" },
  { key: "completion_message", label: "Completion message", placeholder: "Your print is ready" },
  { key: "completion_subtitle", label: "Completion subtitle", placeholder: "Thanks for celebrating with us" },
  { key: "completion_cta_label", label: "Completion button label", placeholder: "Start again" },
  { key: "processing_message", label: "Processing message", placeholder: "Creating your photo…", hint: "Shown under the spinner while the AI works." },
  { key: "retake_button_label", label: "Retake button label", placeholder: "Take another" },
];

/**
 * Stage audio and booth copy.
 *
 * Blank values fall back to the booth defaults, so an operator only overrides
 * what they care about rather than filling in ten fields to change one.
 */
export function AssetsTab() {
  const { draft, update } = useBuilder();
  const { branding, setBranding } = useBranding();
  const assets = draft.assets ?? {};

  const setAudio = (key: keyof AudioAssets, value: string | undefined) =>
    update((current) => ({
      ...current,
      assets: { ...current.assets, audio: { ...current.assets?.audio, [key]: value } },
    }));

  const setCopy = (key: keyof CopyAssets, value: string) =>
    update((current) => ({
      ...current,
      assets: { ...current.assets, copy: { ...current.assets?.copy, [key]: value || undefined } },
    }));

  return (
    <div className="flex flex-col gap-8">
      <CardSection
        eyebrow="Media"
        title="Audio and supporting assets"
        description="Upload optional stage audio. Blank values use the built-in booth defaults."
      >
        <Card className="flex flex-col gap-5 p-5">
          {AUDIO_SLOTS.map((slot) => (
            <UploadField
              key={slot.key}
              label={slot.label}
              hint={slot.hint}
              accept="audio/*"
              preview={false}
              value={assets.audio?.[slot.key]}
              onChange={(value) => setAudio(slot.key, value)}
            />
          ))}
          <p className="rounded-lg bg-ink-800 px-3 py-2 text-xs leading-relaxed text-haze-400">
            Browsers block autoplay until the guest interacts with the page. Booth audio starts on the
            first tap of the welcome screen, so the attract loop stays silent until someone walks up.
          </p>
        </Card>
      </CardSection>

      <CardSection
        eyebrow="Copy"
        title="Print and completion text"
        description="Labels shown during the print flow and on the final screen."
      >
        <Card className="grid gap-5 p-5 sm:grid-cols-2">
          {COPY_SLOTS.map((slot) => (
            <Field key={slot.key} label={slot.label} hint={slot.hint}>
              <Input
                placeholder={slot.placeholder}
                value={assets.copy?.[slot.key] ?? ""}
                onChange={(e) => setCopy(slot.key, e.target.value)}
              />
            </Field>
          ))}
        </Card>
      </CardSection>

      <CardSection
        eyebrow="Final moment"
        title="Completion screen"
        description="Set the closing moment guests see after their photo is ready. Changes save with the event draft."
      >
        <Card className="grid gap-5 p-5 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <UploadField
              label="Completion background image"
              value={branding.completion_background_url}
              onChange={(value) => setBranding({ completion_background_url: value })}
              accept="image/png,image/webp,image/jpeg"
              preview={false}
              hint="Optional full-screen image used only on the final screen. Choose an image with calm, uncluttered space for text."
            />
          </div>
          <div className="rounded-xl border border-ink-600 bg-ink-750 p-4">
            <Toggle
              label="Show event logo"
              hint={branding.logo_url ? "Uses the event logo configured in Branding." : "Upload an event logo in Branding to show it here."}
              checked={branding.completion_show_logo ?? true}
              onChange={(value) => setBranding({ completion_show_logo: value })}
            />
          </div>
          <div className="rounded-xl border border-ink-600 bg-ink-750 p-4">
            <Toggle
              label="Show celebratory decoration"
              hint="Adds a subtle sparkle animation around the completion message."
              checked={branding.completion_show_decor ?? true}
              onChange={(value) => setBranding({ completion_show_decor: value })}
            />
          </div>
        </Card>
      </CardSection>
    </div>
  );
}
